import { useEffect, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { GlobalErrorCode } from '~/lib/constants/global.constants';
import useErrorToast from '~/lib/hooks/useErrorToast';
import {
  isAssignmentActive,
  mergeReassignmentsWithProviders,
  sortProvidersByActiveAndLastName,
} from '~/lib/util/careTeam.util';
import {
  useGetProviderTeamAssignmentsQuery,
  useGetReassignmentsQuery,
} from '~/store/services/clinicalData';
import { useLazyGetAllProvidersV2Query } from '~/store/services/providers';

type ProviderTeamAssignmentWithOptionalStartEnd = Omit<
  ProviderTeamAssignment,
  'assignmentId' | 'startDate' | 'endDate'
> &
  Partial<Pick<ProviderTeamAssignment, 'startDate' | 'endDate'>>;

export type UseGetCareTeamProvider = Pick<
  GetProviderByIdResponse,
  | 'firstName'
  | 'lastName'
  | 'pronouns'
  | 'credentials'
  | 'userStatus'
  | 'outOfOffice'
  | 'displayFirstName'
  | 'displayLastName'
> &
  ProviderTeamAssignmentWithOptionalStartEnd & {
    avatarSrc?: string;
    id: string;
    isEndAssignment: boolean;
    isTemporary: boolean;
    isVoidAssignment: boolean;
    reassignmentId?: string;
    reassignmentDate?: string;
    reassignmentFailureReason?: string | null;
    isIncomingReassignment?: boolean;
    isOutgoingReassignment?: boolean;
  };

export const useGetCareTeam = (
  patientId: string,
  activeTeamMembersOnly: boolean = false,
) => {
  const { providerReassignments } = useFlags<LDFlags>();
  const { showErrorToast } = useErrorToast();
  const {
    data: providerTeamAssignmentsRes,
    isLoading: providerTeamAssignmentsResLoading,
    refetch,
  } = useGetProviderTeamAssignmentsQuery(patientId);
  const {
    data: reassignmentsRes,
    isLoading: reassignmentsLoading,
  } = useGetReassignmentsQuery(patientId, {
    skip: !providerReassignments,
  });
  const [getAllProviderDetails] = useLazyGetAllProvidersV2Query();

  const [providers, setProviders] = useState<UseGetCareTeamProvider[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [treatmentEpisodeId, setTreatmentEpisodeId] = useState<string | null>(
    null,
  );
  const [inquiryId, setInquiryId] = useState<string | null>(null);

  const [isError, setIsError] = useState<boolean>(false);

  const isReassignmentsReady = !providerReassignments || !reassignmentsLoading;

  useEffect(() => {
    if (
      providerTeamAssignmentsRes &&
      !providerTeamAssignmentsResLoading &&
      isReassignmentsReady
    ) {
      const treatmentEpisode = providerTeamAssignmentsRes.treatmentEpisode;
      const inquiry = providerTeamAssignmentsRes.inquiry;

      setTreatmentEpisodeId(treatmentEpisode?.id ?? null);
      setInquiryId(inquiry?.id ?? null);

      let providerListAssignments: ProviderTeamAssignmentWithOptionalStartEnd[] =
        [];
      if (activeTeamMembersOnly && treatmentEpisode?.providerTeamAssignments) {
        providerListAssignments =
          treatmentEpisode.providerTeamAssignments.filter(isAssignmentActive);
      } else {
        providerListAssignments =
          treatmentEpisode?.providerTeamAssignments ??
          inquiry?.providerTeamAssignments ??
          [];
      }

      const activeReassignments = (reassignmentsRes ?? []).filter(
        (r) => !r.isDeleted && r.processedAt === null,
      );
      const incomingProviderIds = activeReassignments
        .filter((r) => r.newProvider !== null)
        .map((r) => r.newProvider!.id);

      const getDetailsForProviders = async () => {
        try {
          const assignmentProviderIds = providerListAssignments?.map(
            (item) => item?.providerId,
          );
          const allProviderIds = [
            ...new Set([...assignmentProviderIds, ...incomingProviderIds]),
          ];
          if (!allProviderIds?.length) {
            return;
          }
          const batchRes = await getAllProviderDetails(
            {
              id: allProviderIds,
              'include-avatar': true,
              'include-out-of-office': true,
              'page-number': 1,
              'page-size': allProviderIds.length,
            },
            true,
          ).unwrap();

          const providerDetailMap = new Map(
            batchRes?.data.map((detail) => [
              detail.userId,
              {
                avatarSrc: detail.avatar?.url,
                credentials: detail.credentials,
                displayFirstName: detail.displayFirstName,
                displayLastName: detail.displayLastName,
                firstName: detail.firstName,
                lastName: detail.lastName,
                outOfOffice: detail.outOfOffice,
                pronouns: detail.pronouns,
                userStatus: detail.userStatus,
              },
            ]),
          );

          const assignmentsWithProviderInfo = providerListAssignments?.map(
            (assignment) => {
              const detail = providerDetailMap.get(assignment.providerId);

              return {
                ...assignment,
                avatarSrc: detail?.avatarSrc,
                credentials: detail?.credentials,
                displayFirstName: detail?.displayFirstName,
                displayLastName: detail?.displayLastName,
                firstName: detail?.firstName,
                id: assignment.providerId,
                isEndAssignment: false,
                isTemporary: assignment.temporary,
                isVoidAssignment: false,
                lastName: detail?.lastName,
                outOfOffice: detail?.outOfOffice,
                pronouns: detail?.pronouns,
                userStatus: detail?.userStatus,
              };
            },
          );

          let enrichedProviders: UseGetCareTeamProvider[] =
            assignmentsWithProviderInfo;

          if (providerReassignments && activeReassignments.length > 0) {
            enrichedProviders = mergeReassignmentsWithProviders(
              assignmentsWithProviderInfo,
              activeReassignments,
              providerDetailMap,
            );

            if (activeTeamMembersOnly) {
              enrichedProviders = enrichedProviders.filter(
                (p) => !p.isIncomingReassignment,
              );
            }
          }

          const sortedProviders =
            sortProvidersByActiveAndLastName(enrichedProviders);

          setProviders(sortedProviders);
        } catch (err) {
          console.error('Error fetching provider details:', err);
          showErrorToast(
            GlobalErrorCode.GET_CARE_TEAM,
            err.status,
            err.data?.message,
          );
          setIsError(true);
          throw new Error('Error fetching provider details');
        } finally {
          setIsLoading(false);
        }
      };

      setIsLoading(true);
      setIsError(false);
      void getDetailsForProviders();
    }
  }, [
    providerTeamAssignmentsRes,
    providerTeamAssignmentsResLoading,
    isReassignmentsReady,
    reassignmentsRes,
    patientId,
    refetch,
  ]);

  return {
    inquiryId,
    isError,
    isLoading,
    providers,
    refetch,
    treatmentEpisodeId,
  };
};
